<template>
  <div class="mt-5">
    <b-container fluid>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Fee Schedule</h1>
        <div v-if="canEdit">
          <button class="btn btn-success mr-1" @click="openCloneModal">
            <b-icon icon="files" class="mr-1"></b-icon> Clone Session
          </button>
          <button class="btn btn-info mr-1" @click="openUploadModal">
            <b-icon icon="cloud-upload" class="mr-1"></b-icon> Upload Excel
          </button>
          <button class="btn btn-danger mr-1" @click="confirmDeleteAllSession" :disabled="!filterSession || filterSession === '__null__'">
            <b-icon icon="trash" class="mr-1"></b-icon> Delete All for Session
          </button>
          <button class="btn btn-primary" @click="openCreateModal">
            <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Fee Item
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="card mb-3">
        <div class="card-body py-2 px-3">
          <div class="row align-items-end">
            <div class="col-md-3">
              <label class="small mb-0">Session</label>
              <b-form-select v-model="filterSession" size="sm" @change="loadFeeSchedules">
                <option value="">All Sessions</option>
                <option value="__null__">No Session (Legacy)</option>
                <option v-for="s in sessionOptions" :key="s" :value="s">{{ s }}</option>
              </b-form-select>
            </div>
            <div class="col-md-3">
              <label class="small mb-0">Prog Code</label>
              <v-select
                v-model="filterProgCode"
                :options="allFilterProgCodes"
                :filter="filterProgCodeSearch"
                placeholder="All Programmes"
                :clearable="true"
                class="vs-sm"
                @input="loadFeeSchedules"
              ></v-select>
            </div>
            <div class="col-md-2">
              <label class="small mb-0">Grant Type</label>
              <b-form-select v-model="filterGrantType" size="sm" @change="loadFeeSchedules">
                <option value="">All</option>
                <option value="RUN">RUN</option>
                <option value="TETFUND">TETFUND</option>
                <option value="OTHERS">OTHERS</option>
              </b-form-select>
            </div>
            <div class="col-md-2">
              <label class="small mb-0">Year</label>
              <b-form-select v-model="filterYear" size="sm" @change="loadFeeSchedules">
                <option value="">All Years</option>
                <option v-for="y in [1,2,3,4,5,6]" :key="y" :value="y">Year {{ y }}</option>
              </b-form-select>
            </div>
            <div class="col-md-1">
              <label class="small mb-0">Status</label>
              <b-form-select v-model="filterStatus" size="sm" @change="loadFeeSchedules">
                <option value="">All</option>
                <option value="DRAFT">DRAFT</option>
                <option value="PUBLISHED">PUBLISHED</option>
              </b-form-select>
            </div>
            <div class="col-md-1">
              <label class="small mb-0">Search</label>
              <b-form-input v-model="filter" size="sm" placeholder="Filter..."></b-form-input>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="row mb-3" v-if="items.length > 0">
        <div class="col-md-4">
          <div class="small-box bg-info">
            <div class="inner">
              <h4>{{ items.length }}</h4>
              <p>Fee Items</p>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="small-box bg-success">
            <div class="inner">
              <h4>{{ formatCurrency(totalAmount) }}</h4>
              <p>Total Amount</p>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="small-box bg-primary">
            <div class="inner">
              <h4>{{ uniqueProgCodes }}</h4>
              <p>Programme Codes</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bulk Action Bar -->
      <div class="alert alert-warning d-flex justify-content-between align-items-center mb-3" v-if="(canEdit || canUpdateStatus) && selectedIds.length > 0">
        <span><strong>{{ selectedIds.length }}</strong> item(s) selected</span>
        <div>
          <button class="btn btn-sm btn-outline-secondary mr-2" @click="clearSelection">Clear Selection</button>
          <button class="btn btn-sm btn-success mr-1" @click="bulkSetStatus('PUBLISHED')" :disabled="bulkUpdatingStatus">
            <b-spinner small v-if="bulkUpdatingStatus" class="mr-1"></b-spinner>
            Set Published
          </button>
          <button class="btn btn-sm btn-warning mr-1" @click="bulkSetStatus('DRAFT')" :disabled="bulkUpdatingStatus">
            <b-spinner small v-if="bulkUpdatingStatus" class="mr-1"></b-spinner>
            Set Draft
          </button>
          <button v-if="canEdit" class="btn btn-sm btn-danger" @click="confirmBulkDelete" :disabled="bulkDeleting">
            <b-spinner small v-if="bulkDeleting" class="mr-1"></b-spinner>
            {{ bulkDeleting ? 'Deleting...' : 'Delete Selected' }}
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="card mb-3">
        <div class="card-body p-0">
          <b-table
            :items="items"
            :fields="tableFields"
            :busy="loading"
            :filter="filter"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #head(selected)>
              <b-form-checkbox :checked="allSelected" @change="toggleSelectAll" :disabled="items.length === 0"></b-form-checkbox>
            </template>
            <template #cell(selected)="data">
              <b-form-checkbox :checked="selectedIds.includes(data.item.id)" @change="toggleSelect(data.item.id)"></b-form-checkbox>
            </template>
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading fee schedule...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No fee items found. Use filters or add new items.</div>
            </template>
            <template #cell(amount)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template #cell(session)="data">
              {{ data.value || 'N/A' }}
            </template>
            <template #cell(status)="data">
              <b-badge :variant="data.value === 'PUBLISHED' ? 'success' : 'warning'">{{ data.value || 'PUBLISHED' }}</b-badge>
            </template>
            <template #cell(actions)="data">
              <button v-if="canEdit" class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="saving">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button v-if="canEdit" class="btn btn-sm btn-danger mr-1" @click="confirmDelete(data.item)" :disabled="deletingId === data.item.id">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
              <button
                v-if="canUpdateStatus && !canEdit"
                class="btn btn-sm"
                :class="data.item.status === 'PUBLISHED' ? 'btn-outline-warning' : 'btn-outline-success'"
                @click="toggleRowStatus(data.item)"
                :disabled="bulkUpdatingStatus"
              >
                {{ data.item.status === 'PUBLISHED' ? 'Set Draft' : 'Publish' }}
              </button>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center" v-if="items.length > perPage">
        <small class="text-muted">{{ items.length }} items total</small>
        <b-pagination
          v-model="currentPage"
          :total-rows="items.length"
          :per-page="perPage"
          size="sm"
        ></b-pagination>
      </div>

      <!-- ==================== CREATE/EDIT MODAL ==================== -->
      <b-modal
        v-model="showModal"
        :title="editMode ? 'Edit Fee Item' : 'Add Fee Item'"
        @ok.prevent="saveFeeSchedule"
        :ok-disabled="saving"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>

        <div class="row">
          <div class="col-md-12">
            <b-form-group label="Fee Item Name *">
              <b-form-input v-model="form.item" :disabled="saving" placeholder="e.g. Tuition Fee"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Amount *">
              <b-form-input type="number" v-model.number="form.amount" :disabled="saving" min="0"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Installment">
              <b-form-input type="number" v-model.number="form.installment" :disabled="saving" min="0"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-12">
            <b-form-group label="Programme Code *">
              <v-select
                v-model="form.prog_code"
                :options="programmeOptions"
                :get-option-label="o => o.code + ' - ' + o.programme"
                :reduce="o => o.code"
                :filter="filterProgOptions"
                :disabled="saving"
                placeholder="Type to search programme..."
                :taggable="true"
                :create-option="val => ({ code: val, programme: '' })"
              ></v-select>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Grant Type *">
              <b-form-select v-model="form.grant_type" :disabled="saving">
                <option value="RUN">RUN</option>
                <option value="TETFUND">TETFUND</option>
                <option value="OTHERS">OTHERS</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Year *">
              <b-form-select v-model.number="form.year" :disabled="saving">
                <option v-for="y in [1,2,3,4,5,6]" :key="y" :value="y">Year {{ y }}</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Session">
              <b-form-select v-model="form.session" :disabled="saving">
                <option :value="null">-- None --</option>
                <option v-for="s in sessionOptions" :key="s" :value="s">{{ s }}</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Status *">
              <b-form-select v-model="form.status" :disabled="saving">
                <option value="DRAFT">DRAFT</option>
                <option value="PUBLISHED">PUBLISHED</option>
              </b-form-select>
            </b-form-group>
          </div>
        </div>

        <!-- Prog Code Helper -->
        <div class="alert alert-light small mt-2 mb-0" v-if="programmes.length > 0">
          <strong>Prog Code format:</strong> CODE*DEGREE*TYPE (e.g. MCM*PHD*FT).
          Build from programmes list: choose a programme, its code + degree + type form the prog_code.
        </div>
      </b-modal>

      <!-- ==================== CLONE MODAL ==================== -->
      <b-modal
        v-model="showCloneModal"
        title="Clone Fee Schedule"
        @ok.prevent="cloneSession"
        :ok-disabled="cloning || !cloneForm.source_session || !cloneForm.target_session || (cloneMode === 'degree' && !cloneForm.source_degree) || (cloneMode === 'specific' && !cloneForm.source_prog_code)"
        no-close-on-backdrop
        size="lg"
      >
        <template #modal-ok>
          <b-spinner small v-if="cloning" class="mr-1"></b-spinner>
          {{ cloning ? 'Cloning...' : 'Clone' }}
        </template>
        <p class="text-muted small">
          Copy fee items from one session to another. Clone a specific programme, all programmes of a degree type, or everything.
        </p>

        <!-- Clone Mode Tabs -->
        <b-form-group label="Clone Mode" class="mb-3">
          <b-form-radio-group v-model="cloneMode" :disabled="cloning" buttons button-variant="outline-primary" size="sm">
            <b-form-radio value="all">All Programmes</b-form-radio>
            <b-form-radio value="degree">By Degree</b-form-radio>
            <b-form-radio value="specific">Specific Programme</b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Source Session *">
              <b-form-select v-model="cloneForm.source_session" :disabled="cloning">
                <option :value="null">-- Select source --</option>
                <option v-for="s in sessionOptions" :key="'src-'+s" :value="s">{{ s }}</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Target Session *">
              <b-form-select v-model="cloneForm.target_session" :disabled="cloning">
                <option :value="null">-- Select target --</option>
                <option v-for="s in sessionOptions" :key="'tgt-'+s" :value="s">{{ s }}</option>
              </b-form-select>
            </b-form-group>
          </div>

          <!-- By Degree mode -->
          <template v-if="cloneMode === 'degree'">
            <div class="col-md-6">
              <b-form-group label="Degree *" description="Clone all programmes with this degree">
                <b-form-select v-model="cloneForm.source_degree" :disabled="cloning">
                  <option :value="null">-- Select degree --</option>
                  <option v-for="d in degreeOptions" :key="d" :value="d">{{ d }}</option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Programme Type" description="FT, PT, or leave empty for all">
                <b-form-select v-model="cloneForm.source_prog_type" :disabled="cloning">
                  <option :value="null">All (FT + PT)</option>
                  <option value="FT">Full Time (FT)</option>
                  <option value="PT">Part Time (PT)</option>
                </b-form-select>
              </b-form-group>
            </div>
          </template>

          <!-- Specific Programme mode -->
          <template v-if="cloneMode === 'specific'">
            <div class="col-md-6">
              <b-form-group label="Source Programme *">
                <v-select
                  v-model="cloneForm.source_prog_code"
                  :options="programmeOptions"
                  :get-option-label="o => o.code + ' - ' + o.programme"
                  :reduce="o => o.code"
                  :filter="filterProgOptions"
                  :disabled="cloning"
                  placeholder="Select programme"
                ></v-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Target Programme" description="Leave empty to keep same code">
                <v-select
                  v-model="cloneForm.target_prog_code"
                  :options="programmeOptions"
                  :get-option-label="o => o.code + ' - ' + o.programme"
                  :reduce="o => o.code"
                  :filter="filterProgOptions"
                  :disabled="cloning"
                  placeholder="Same as source"
                ></v-select>
              </b-form-group>
            </div>
          </template>

          <div class="col-md-6">
            <b-form-group label="Source Grant Type (optional)">
              <b-form-select v-model="cloneForm.source_grant_type" :disabled="cloning">
                <option :value="null">All</option>
                <option value="RUN">RUN</option>
                <option value="TETFUND">TETFUND</option>
                <option value="OTHERS">OTHERS</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Target Grant Type (optional)">
              <b-form-select v-model="cloneForm.target_grant_type" :disabled="cloning">
                <option :value="null">Same as source</option>
                <option value="RUN">RUN</option>
                <option value="TETFUND">TETFUND</option>
                <option value="OTHERS">OTHERS</option>
              </b-form-select>
            </b-form-group>
          </div>
        </div>

        <!-- Summary of what will be cloned -->
        <div class="alert alert-info small mb-0">
          <b-icon icon="info-circle-fill" class="mr-1"></b-icon>
          <strong>Will clone:</strong>
          <span v-if="cloneMode === 'all'">All fee items</span>
          <span v-else-if="cloneMode === 'degree'">
            All <strong>{{ cloneForm.source_degree || '?' }}</strong> programmes
            <span v-if="cloneForm.source_prog_type"> ({{ cloneForm.source_prog_type }} only)</span>
          </span>
          <span v-else-if="cloneMode === 'specific'">
            Programme <strong>{{ cloneForm.source_prog_code || '?' }}</strong>
            <span v-if="cloneForm.target_prog_code"> → <strong>{{ cloneForm.target_prog_code }}</strong></span>
          </span>
          <span v-if="cloneForm.source_grant_type"> | Grant: {{ cloneForm.source_grant_type }}</span>
          <span v-if="cloneForm.source_session && cloneForm.target_session"> | {{ cloneForm.source_session }} → {{ cloneForm.target_session }}</span>
          <span v-if="cloneForm.target_grant_type"> | Target grant: {{ cloneForm.target_grant_type }}</span>
        </div>
      </b-modal>

      <!-- ==================== UPLOAD MODAL ==================== -->
      <b-modal
        v-model="showUploadModal"
        title="Upload Fee Schedule from Excel"
        @ok.prevent="uploadFeeSchedule"
        :ok-disabled="uploading || !uploadFile || !uploadSession"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="uploading" class="mr-1"></b-spinner>
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </template>
        <b-form-group label="Session *">
          <b-form-select v-model="uploadSession" :disabled="uploading">
            <option :value="null">-- Select session --</option>
            <option v-for="s in sessionOptions" :key="'up-'+s" :value="s">{{ s }}</option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Excel File *">
          <b-form-file
            v-model="uploadFile"
            :disabled="uploading"
            accept=".xlsx,.xls,.csv"
            placeholder="Choose file..."
          ></b-form-file>
        </b-form-group>
        <div class="alert alert-info small mb-0">
          <strong>Required Excel columns:</strong> item, amount, prog_code, grant_type, year, installment
          <br>
          <a href="#" @click.prevent="downloadTemplate" class="mt-1 d-inline-block mr-3">
            <b-icon icon="download" class="mr-1"></b-icon>Download sample template (.csv)
          </a>
          <a href="#" @click.prevent="downloadProgCodes" class="mt-1 d-inline-block">
            <b-icon icon="list-ul" class="mr-1"></b-icon>Download prog codes reference (.csv)
          </a>
        </div>
      </b-modal>

    </b-container>
  </div>
</template>

<script>
export default {
  name: "FeeScheduleManagement",
  data() {
    return {
      items: [],
      programmes: [],
      loading: false,
      saving: false,
      cloning: false,
      uploading: false,
      deletingId: null,
      selectedIds: [],
      bulkDeleting: false,
      bulkUpdatingStatus: false,
      deletingAll: false,
      filter: "",
      filterSession: "",
      filterProgCode: "",
      filterGrantType: "",
      filterYear: "",
      filterStatus: "",
      currentPage: 1,
      perPage: 25,
      showModal: false,
      showCloneModal: false,
      showUploadModal: false,
      editMode: false,
      editId: null,
      form: this.emptyForm(),
      cloneMode: "all",
      cloneForm: { source_session: null, target_session: null, source_prog_code: '', target_prog_code: '', source_grant_type: null, target_grant_type: null, source_degree: null, source_prog_type: null },
      uploadFile: null,
      uploadSession: null,
      fields: [
        { key: "item", label: "Fee Item", sortable: true },
        { key: "amount", label: "Amount", sortable: true, class: "text-right" },
        { key: "prog_code", label: "Prog Code", sortable: true },
        { key: "grant_type", label: "Grant Type", sortable: true },
        { key: "year", label: "Year", sortable: true, class: "text-center" },
        { key: "session", label: "Session", sortable: true },
        { key: "status", label: "Status", sortable: true, class: "text-center" },
        { key: "installment", label: "Installment", sortable: true, class: "text-center" },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "100px" } },
      ],
    };
  },
  computed: {
    canEdit() {
      let roles = this.$store.state.user.roles || [];
      return roles.some((r) => ["SUPERADMIN", "ICT", "ADMIN", "BURSARY"].includes(r));
    },
    canUpdateStatus() {
      let roles = this.$store.state.user.roles || [];
      return roles.some((r) => ["SUPERADMIN", "ICT", "ADMIN", "BURSARY"].includes(r));
    },
    settings() {
      return this.$store.state.settings || [];
    },
    sessionOptions() {
      if (!this.settings.length) return [];
      return [...new Set(this.settings.map((s) => s.session_name))];
    },
    activeSession() {
      if (!this.settings.length) return null;
      var active = this.settings.find((s) => s.status === "active");
      return active ? active.session_name : null;
    },
    tableFields() {
      let fields = this.fields;
      if (!this.canEdit) {
        fields = fields.filter((f) => f.key !== "actions");
      }
      if (this.canEdit || this.canUpdateStatus) {
        fields = [
          { key: "selected", label: "", class: "text-center", thStyle: { width: "40px" } },
          ...fields,
        ];
      }
      return fields;
    },
    allSelected() {
      return this.items.length > 0 && this.selectedIds.length === this.items.length;
    },
    totalAmount() {
      return this.items.reduce((sum, i) => sum + (i.amount || 0), 0);
    },
    uniqueProgCodes() {
      return new Set(this.items.map((i) => i.prog_code)).size;
    },
    progCodeOptions() {
      if (!this.items.length && !this.allProgCodes.length) return [];
      return [...new Set(this.items.map((i) => i.prog_code))].sort();
    },
    allFilterProgCodes() {
      var fromItems = this.items.map(function (i) { return i.prog_code; });
      var fromProgs = this.programmes.map(function (p) { return p.code; }).filter(Boolean);
      return [...new Set(fromItems.concat(fromProgs))].sort();
    },
    allProgCodes() {
      if (!this.programmes.length) return [];
      return [...new Set(this.programmes.map((p) => p.code).filter(Boolean))].sort();
    },
    programmeOptions() {
      return this.programmes.filter(function(p) { return p.code; });
    },
    degreeOptions() {
      var degrees = this.programmes.map(function (p) { return p.degree; }).filter(Boolean);
      return [...new Set(degrees)].sort();
    },
  },
  created() {
    this.$store.dispatch("getSettings").then(() => {
      if (this.activeSession) {
        this.filterSession = this.activeSession;
      }
      this.loadFeeSchedules();
    });
    this.loadProgrammes();
  },
  methods: {
    emptyForm() {
      return {
        item: "",
        amount: "",
        prog_code: "",
        grant_type: "RUN",
        year: 1,
        installment: 0,
        session: null,
        status: "DRAFT",
      };
    },
    loadProgrammes() {
      axios.get("admin/programmes").then((res) => {
        if (res.data.msg === "success") this.programmes = res.data.data;
      }).catch(() => {});
    },
    filterProgCodeSearch(options, search) {
      var s = search.toLowerCase();
      return options.filter(function (o) {
        return o.toLowerCase().indexOf(s) > -1;
      });
    },
    loadFeeSchedules() {
      this.loading = true;
      this.selectedIds = [];
      let params = {};
      if (this.filterSession === "__null__") {
        // special: legacy rows with no session — handled client-side after load
      } else if (this.filterSession) {
        params.session = this.filterSession;
      }
      if (this.filterProgCode) params.prog_code = this.filterProgCode;
      if (this.filterGrantType) params.grant_type = this.filterGrantType;
      if (this.filterYear) params.year = this.filterYear;
      if (this.filterStatus) params.status = this.filterStatus;

      axios.get("admin/fee-schedules", { params })
        .then((res) => {
          if (res.data.msg === "success") {
            let data = res.data.data;
            if (this.filterSession === "__null__") {
              data = data.filter((i) => !i.session);
            }
            this.items = data;
          }
        })
        .catch((err) => {
          var msg = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Failed to load fee schedule";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.loading = false));
    },
    formatCurrency(val) {
      return "₦" + Number(val || 0).toLocaleString("en-NG");
    },

    // CRUD
    openCreateModal() {
      this.editMode = false;
      this.editId = null;
      this.form = this.emptyForm();
      if (this.filterSession && this.filterSession !== "__null__") {
        this.form.session = this.filterSession;
      } else if (this.activeSession) {
        this.form.session = this.activeSession;
      }
      this.showModal = true;
    },
    openEditModal(item) {
      this.editMode = true;
      this.editId = item.id;
      this.form = {
        item: item.item,
        amount: item.amount,
        prog_code: item.prog_code,
        grant_type: item.grant_type,
        year: item.year,
        installment: item.installment || 0,
        session: item.session || null,
        status: item.status || "PUBLISHED",
      };
      this.showModal = true;
    },
    saveFeeSchedule() {
      let progCode = this.form.prog_code;
      if (!this.form.item || !this.form.amount || !progCode) {
        Toast.fire({ icon: "warning", title: "Fill in all required fields" });
        return;
      }
      this.saving = true;
      let payload = {
        item: this.form.item,
        amount: this.form.amount,
        prog_code: progCode,
        grant_type: this.form.grant_type,
        year: this.form.year,
        installment: this.form.installment || 0,
        session: this.form.session,
        status: this.form.status,
      };

      let req;
      if (this.editMode) {
        req = axios.put("admin/fee-schedules/" + this.editId, payload);
      } else {
        req = axios.post("admin/fee-schedules", payload);
      }
      req.then((res) => {
        if (res.data.msg === "success") {
          Toast.fire({ icon: "success", title: this.editMode ? "Fee item updated" : "Fee item created" });
          this.showModal = false;
          this.loadFeeSchedules();
        } else {
          Toast.fire({ icon: "error", title: res.data.message || "Operation failed" });
        }
      })
        .catch((err) => {
          var data = err.response && err.response.data;
          if (data && data.duplicate) {
            Swal.fire({
              icon: "warning",
              title: "Duplicate Fee Item",
              text: data.message,
            });
          } else {
            Toast.fire({ icon: "error", title: (data && data.message) || "An error occurred" });
          }
        })
        .finally(() => (this.saving = false));
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Delete Fee Item?",
        text: 'Delete "' + item.item + '" (' + item.prog_code + ', Year ' + item.year + ')?',
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingId = item.id;
          axios.delete("admin/fee-schedules/" + item.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Fee item deleted" });
                this.loadFeeSchedules();
              }
            })
            .catch(() => Toast.fire({ icon: "error", title: "Failed to delete" }))
            .finally(() => (this.deletingId = null));
        }
      });
    },

    // Selection
    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
    },
    toggleSelectAll(checked) {
      if (checked) {
        this.selectedIds = this.items.map((i) => i.id);
      } else {
        this.selectedIds = [];
      }
    },
    clearSelection() {
      this.selectedIds = [];
    },
    confirmBulkDelete() {
      Swal.fire({
        title: "Delete Selected Items?",
        text: "This will permanently delete " + this.selectedIds.length + " selected fee item(s).",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete them",
      }).then((result) => {
        if (result.isConfirmed) {
          this.bulkDeleting = true;
          axios.post("admin/fee-schedules/bulk-delete", { ids: this.selectedIds })
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: res.data.message });
                this.selectedIds = [];
                this.loadFeeSchedules();
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Bulk delete failed" }))
            .finally(() => (this.bulkDeleting = false));
        }
      });
    },
    confirmDeleteAllSession() {
      if (!this.filterSession || this.filterSession === "__null__") return;
      Swal.fire({
        title: "Delete All for Session?",
        text: 'This will permanently delete ALL fee items for session "' + this.filterSession + '".',
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete all",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingAll = true;
          axios.post("admin/fee-schedules/delete-session", { session: this.filterSession })
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: res.data.message });
                this.selectedIds = [];
                this.loadFeeSchedules();
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Delete failed" }))
            .finally(() => (this.deletingAll = false));
        }
      });
    },

    // Clone
    openCloneModal() {
      this.cloneMode = "all";
      this.cloneForm = { source_session: null, target_session: null, source_prog_code: '', target_prog_code: '', source_grant_type: null, target_grant_type: null, source_degree: null, source_prog_type: null };
      this.showCloneModal = true;
    },
    cloneSession() {
      this.cloning = true;
      var payload = {
        source_session: this.cloneForm.source_session,
        target_session: this.cloneForm.target_session,
        source_grant_type: this.cloneForm.source_grant_type,
        target_grant_type: this.cloneForm.target_grant_type,
      };
      if (this.cloneMode === "specific") {
        payload.source_prog_code = this.cloneForm.source_prog_code;
        payload.target_prog_code = this.cloneForm.target_prog_code;
      } else if (this.cloneMode === "degree") {
        payload.source_degree = this.cloneForm.source_degree;
        payload.source_prog_type = this.cloneForm.source_prog_type;
      }
      axios.post("admin/fee-schedules/clone", payload)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: res.data.message });
            this.showCloneModal = false;
            this.filterSession = this.cloneForm.target_session;
            this.loadFeeSchedules();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Clone failed" });
          }
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Clone failed" });
        })
        .finally(() => (this.cloning = false));
    },

    // Upload
    openUploadModal() {
      this.uploadFile = null;
      this.uploadSession = null;
      if (this.filterSession && this.filterSession !== "__null__") {
        this.uploadSession = this.filterSession;
      }
      this.showUploadModal = true;
    },
    uploadFeeSchedule() {
      if (!this.uploadFile || !this.uploadSession) return;
      this.uploading = true;
      let formData = new FormData();
      formData.append("file", this.uploadFile);
      formData.append("session", this.uploadSession);
      axios.post("admin/fee-schedules/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: res.data.message });
            this.showUploadModal = false;
            this.filterSession = this.uploadSession;
            this.loadFeeSchedules();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Upload failed" });
          }
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Upload failed" });
        })
        .finally(() => (this.uploading = false));
    },

    filterProgOptions(options, search) {
      var s = search.toLowerCase();
      return options.filter(function(o) {
        return (o.code || '').toLowerCase().indexOf(s) > -1
          || (o.programme || '').toLowerCase().indexOf(s) > -1
          || (o.degree || '').toLowerCase().indexOf(s) > -1;
      });
    },

    // Download template
    downloadProgCodes() {
      if (!this.programmes.length) {
        Toast.fire({ icon: "warning", title: "Programmes not loaded yet" });
        return;
      }
      var header = "prog_code,programme,degree,type";
      var rows = this.programmes
        .filter(function (p) { return p.code; })
        .map(function (p) {
          var name = (p.programme || "").replace(/,/g, " ");
          return p.code + "," + name + "," + (p.degree || "") + "," + (p.prog_type || "");
        });
      var csv = header + "\n" + rows.join("\n");
      var blob = new Blob([csv], { type: "text/csv" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = "prog_codes_reference.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    downloadTemplate() {
      var header = "item,amount,prog_code,grant_type,year,installment";
      var rows = [
        "Tuition Fee,150000,STA*MSC*FT,OTHERS,1,0",
        "Acceptance Fee,30000,STA*MSC*FT,OTHERS,1,0",
        "Lab Fee,25000,CMP*PHD*FT,RUN,1,0",
        "Library Fee,10000,CMP*PHD*FT,RUN,2,0",
      ];
      var csv = header + "\n" + rows.join("\n");
      var blob = new Blob([csv], { type: "text/csv" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = "fee_schedule_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    },

    // Status updates
    bulkSetStatus(status) {
      Swal.fire({
        title: "Update Status?",
        text: "Set " + this.selectedIds.length + " item(s) to " + status + "?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, update",
      }).then((result) => {
        if (result.isConfirmed) {
          this.bulkUpdatingStatus = true;
          axios.post("admin/fee-schedules/bulk-status", { ids: this.selectedIds, status })
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: res.data.message });
                this.selectedIds = [];
                this.loadFeeSchedules();
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Status update failed" }))
            .finally(() => (this.bulkUpdatingStatus = false));
        }
      });
    },
    toggleRowStatus(item) {
      let newStatus = item.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
      this.bulkUpdatingStatus = true;
      axios.post("admin/fee-schedules/bulk-status", { ids: [item.id], status: newStatus })
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: res.data.message });
            this.loadFeeSchedules();
          }
        })
        .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Status update failed" }))
        .finally(() => (this.bulkUpdatingStatus = false));
    },
  },
};
</script>

<style scoped>
.small-box {
  border-radius: 0.25rem;
  padding: 20px;
  color: #fff;
  margin-bottom: 10px;
}
.small-box .inner h4 {
  font-size: 1.4rem;
  font-weight: 700;
}
.small-box .inner p {
  margin: 0;
  font-size: 0.85rem;
}
.vs-sm >>> .vs__dropdown-toggle {
  min-height: 31px;
  font-size: 0.875rem;
  padding: 0;
}
.vs-sm >>> .vs__search {
  font-size: 0.875rem;
  margin: 2px 0 2px 4px;
}
.vs-sm >>> .vs__selected {
  font-size: 0.875rem;
  margin: 2px 4px;
}
</style>
